const DEFAULT_API_BASE_URL = "http://localhost:3100";

export function apiBaseUrl(): string {
	const configured = import.meta.env.VITE_API_BASE_URL;
	if (configured === undefined || configured === null) return DEFAULT_API_BASE_URL;
	return configured.trim().replace(/\/$/, "");
}

type QueryValue = string | number | boolean | null | undefined;

export type QueryParams = Record<string, QueryValue>;

export class HttpError extends Error {
	readonly status: number;
	readonly body: unknown;

	constructor(message: string, status: number, body: unknown) {
		super(message);
		this.name = "HttpError";
		this.status = status;
		this.body = body;
	}
}

function isBodyInit(value: unknown): value is BodyInit {
	return (
		typeof value === "string" ||
		value instanceof FormData ||
		value instanceof URLSearchParams ||
		value instanceof Blob ||
		value instanceof ArrayBuffer ||
		ArrayBuffer.isView(value)
	);
}

function buildUrl(path: string, query?: QueryParams) {
	const base = apiBaseUrl() || window.location.origin;
	const url =
		path.startsWith("http://") || path.startsWith("https://") ? new URL(path) : new URL(path, base);

	if (query) {
		for (const [key, value] of Object.entries(query)) {
			if (value === undefined || value === null || value === "") {
				continue;
			}
			url.searchParams.set(key, String(value));
		}
	}

	return url;
}

export async function requestJson<T>(
	path: string,
	init: Omit<RequestInit, "body"> & {
		body?: unknown;
		query?: QueryParams;
	} = {},
): Promise<T> {
	const { query, body, headers, ...requestInit } = init;
	const normalizedHeaders = new Headers(headers);
	const requestBody =
		body === undefined ? undefined : isBodyInit(body) ? body : JSON.stringify(body);

	if (requestBody !== undefined && !isBodyInit(body) && !normalizedHeaders.has("content-type")) {
		normalizedHeaders.set("content-type", "application/json");
	}

	if (!normalizedHeaders.has("accept")) {
		normalizedHeaders.set("accept", "application/json");
	}

	const response = await fetch(buildUrl(path, query), {
		...requestInit,
		credentials: "include",
		headers: normalizedHeaders,
		body: requestBody,
	});

	if (response.status === 204) {
		return undefined as T;
	}

	const contentType = response.headers.get("content-type") ?? "";
	let payload: unknown = null;

	if (contentType.includes("application/json")) {
		payload = await response.json().catch(() => null);
	} else {
		const text = await response.text().catch(() => "");
		payload = text.length > 0 ? text : null;
	}

	if (!response.ok) {
		const message =
			typeof payload === "object" &&
			payload !== null &&
			("error" in payload || "message" in payload)
				? String(
						(payload as { error?: string; message?: string }).error ??
							(payload as { message?: string }).message,
					)
				: `Request failed with status ${response.status}`;
		throw new HttpError(message, response.status, payload);
	}

	return payload as T;
}

export function isHttpError(error: unknown): error is HttpError {
	return error instanceof HttpError;
}
